import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion as Motion } from "framer-motion";
import { toast } from "react-toastify";

import api from "@/utils/axios";
import i18n from "@/i18n";
import cls from "./CreateMemberGender.module.scss";
import { GENDERS } from "@/enums/MemberEnums";
import { useAuth } from "@/app/auth-context-state";

/* Welcome-style hero placeholders (q4) */
import mrImg from "@/assets/UI/memberCreate/q4Mr.webp";
import mrMImg from "@/assets/UI/memberCreate/q4MrM.webp";
import mrsImg from "@/assets/UI/memberCreate/q4Mrs.webp";
import mrsMImg from "@/assets/UI/memberCreate/q4MrsM.webp";

/* ✅ Replace icon components with webp assets */
import manIcon from "@/assets/UI/man.webp";
import womanIcon from "@/assets/UI/woman.webp";
import non_binaryIcon from "@/assets/UI/non_binary.webp";
import trans_manIcon from "@/assets/UI/trans_man.webp";
import trans_womanIcon from "@/assets/UI/trans_woman.webp";
import genderfluidIcon from "@/assets/UI/genderfluid.webp";
import self_describeIcon from "@/assets/UI/self_describe.webp";

/* ✅ NEW: card images (after form) */
import manImg from "@/assets/UI/memberCreate/q4Man.webp";
import womanImg from "@/assets/UI/memberCreate/q4Woman.webp";
import nonBImg from "@/assets/UI/memberCreate/q4NonB.webp";
import tManImg from "@/assets/UI/memberCreate/q4TMan.webp";
import tWomanImg from "@/assets/UI/memberCreate/q4TWoman.webp";
import gFluidImg from "@/assets/UI/memberCreate/q4GFluid.webp";
import sDImg from "@/assets/UI/memberCreate/q4SD.webp";

/* ---- Framer animations (same feel as Welcome) ---- */
const floatSlow = {
  hidden: { opacity: 0.55 },
  show: {
    opacity: [0.55, 1, 0.55],
    scale: [1, 1.03, 1],
    transition: { duration: 10, ease: "easeInOut", repeat: Infinity },
  },
};

const floatSlowAlt = {
  hidden: { opacity: 0.55 },
  show: {
    opacity: [0.72, 1, 0.72],
    scale: [1.03, 1, 1.03],
    x: [0, -6, 0],
    transition: { duration: 12, ease: "easeInOut", repeat: Infinity },
  },
};

const ICON_BY_GENDER = {
  man: manIcon,
  woman: womanIcon,
  non_binary: non_binaryIcon,
  trans_man: trans_manIcon,
  trans_woman: trans_womanIcon,
  genderfluid: genderfluidIcon,
  self_describe: self_describeIcon,
  not_select: self_describeIcon,
};

const GENDER_META = {
  man: {
    IconSrc: ICON_BY_GENDER.man,
    labelKey: "onboarding.gender.options.man",
  },
  woman: {
    IconSrc: ICON_BY_GENDER.woman,
    labelKey: "onboarding.gender.options.woman",
  },
  non_binary: {
    IconSrc: ICON_BY_GENDER.non_binary,
    labelKey: "onboarding.gender.options.non_binary",
  },
  trans_man: {
    IconSrc: ICON_BY_GENDER.trans_man,
    labelKey: "onboarding.gender.options.trans_man",
  },
  trans_woman: {
    IconSrc: ICON_BY_GENDER.trans_woman,
    labelKey: "onboarding.gender.options.trans_woman",
  },
  genderfluid: {
    IconSrc: ICON_BY_GENDER.genderfluid,
    labelKey: "onboarding.gender.options.genderfluid",
  },
  self_describe: {
    IconSrc: ICON_BY_GENDER.self_describe,
    labelKey: "onboarding.gender.options.self_describe",
  },
  not_select: {
    IconSrc: ICON_BY_GENDER.not_select,
    labelKey: "onboarding.gender.options.not_select",
  },
};

export default function CreateMemberGender() {
  const navigate = useNavigate();
  const { refreshMe } = useAuth();

  const [submitting, setSubmitting] = useState(false);
  const [choice, setChoice] = useState("");
  const [customGender, setCustomGender] = useState("");

  const t = (k, o) => i18n.t(k, o);

  const MrD = useMemo(() => mrImg, []);
  const MrsD = useMemo(() => mrsImg, []);
  const MrM = useMemo(() => mrMImg, []);
  const MrsM = useMemo(() => mrsMImg, []);

  const title = t("onboarding.gender.title", { defaultValue: "Gender" });
  const subtitle = t("onboarding.gender.question", {
    defaultValue: "What gender best describes you?",
  });

  const cta = t("common.continue", { defaultValue: "Continue" });

  const genders =
    Array.isArray(GENDERS) && GENDERS.length
      ? GENDERS
      : [
          "man",
          "woman",
          "non_binary",
          "trans_man",
          "trans_woman",
          "genderfluid",
          "self_describe",
          "not_select",
        ];

  const options = genders.map((value) => {
    const icon = ICON_BY_GENDER[value] || self_describeIcon;
    const labelKey = `onboarding.gender.options.${value}`;
    const hintKey = `onboarding.gender.hints.${value}`;

    return {
      value,
      icon,
      title: t(labelKey, { defaultValue: value }),
      hint:
        t(hintKey, { defaultValue: "" }) ||
        t("onboarding.gender.hint", {
          defaultValue: "Choose the option that feels right for you.",
        }),
    };
  });

  const needsCustom = choice === "self_describe";

  const cards = [
    {
      key: "man",
      img: manImg,
      IconSrc: GENDER_META.man.IconSrc,
      title: i18n.t("onboarding.gender.cards.man.title"),
      desc: i18n.t("onboarding.gender.cards.man.desc"),
    },
    {
      key: "woman",
      img: womanImg,
      IconSrc: GENDER_META.woman.IconSrc,
      title: i18n.t("onboarding.gender.cards.woman.title"),
      desc: i18n.t("onboarding.gender.cards.woman.desc"),
    },
    {
      key: "non_binary",
      img: nonBImg,
      IconSrc: GENDER_META.non_binary.IconSrc,
      title: i18n.t("onboarding.gender.cards.non_binary.title"),
      desc: i18n.t("onboarding.gender.cards.non_binary.desc"),
    },
    {
      key: "trans_man",
      img: tManImg,
      IconSrc: GENDER_META.trans_man.IconSrc,
      title: i18n.t("onboarding.gender.cards.trans_man.title"),
      desc: i18n.t("onboarding.gender.cards.trans_man.desc"),
    },
    {
      key: "trans_woman",
      img: tWomanImg,
      IconSrc: GENDER_META.trans_woman.IconSrc,
      title: i18n.t("onboarding.gender.cards.trans_woman.title"),
      desc: i18n.t("onboarding.gender.cards.trans_woman.desc"),
    },
    {
      key: "genderfluid",
      img: gFluidImg,
      IconSrc: GENDER_META.genderfluid.IconSrc,
      title: i18n.t("onboarding.gender.cards.genderfluid.title"),
      desc: i18n.t("onboarding.gender.cards.genderfluid.desc"),
    },
    {
      key: "self_describe",
      img: sDImg,
      IconSrc: GENDER_META.self_describe.IconSrc,
      title: i18n.t("onboarding.gender.cards.self_describe.title"),
      desc: i18n.t("onboarding.gender.cards.self_describe.desc"),
    },
  ];

  async function onSubmit(e) {
    e.preventDefault();

    if (!choice) {
      toast.info(
        t("onboarding.gender.pickOne", { defaultValue: "Please pick one." }),
      );
      return;
    }

    if (needsCustom && !customGender.trim()) {
      toast.info(
        t("onboarding.gender.selfDescribeRequired", {
          defaultValue: "Please type how you want to describe your gender.",
        }),
      );
      return;
    }

    setSubmitting(true);
    try {
      const payload = { gender: choice };
      if (needsCustom) payload.customGender = customGender.trim();

      const { data } = await api.post("/v1/member-create/gender", payload, {
        skipToast: true,
      });

      await refreshMe();
      navigate(data?.next || "/member/relation", { replace: true });
    } catch (err) {
      console.error("update gender failed:", err);
      toast.error(
        t("onboarding.gender.error", { defaultValue: "Something went wrong." }),
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className={cls.page}>
      {/* Fixed background stage */}
      <section className={cls.heroStage} aria-hidden="true">
        <div className={cls.mobileOnly}>
          <Motion.img
            className={`${cls.heroImg} ${cls.z1}`}
            src={MrM}
            alt=""
            variants={floatSlow}
            initial="hidden"
            animate="show"
            draggable="false"
          />
          <Motion.img
            className={`${cls.heroImg} ${cls.z3}`}
            src={MrsM}
            alt=""
            variants={floatSlowAlt}
            initial="hidden"
            animate="show"
            draggable="false"
          />
        </div>

        <div className={cls.desktopOnly}>
          <Motion.img
            className={`${cls.heroImg} ${cls.z1}`}
            src={MrD}
            alt=""
            variants={floatSlow}
            initial="hidden"
            animate="show"
            draggable="false"
          />
          <Motion.img
            className={`${cls.heroImg} ${cls.z3}`}
            src={MrsD}
            alt=""
            variants={floatSlowAlt}
            initial="hidden"
            animate="show"
            draggable="false"
          />
        </div>

        <div className={cls.stageTint} />
      </section>

      {/* ✅ Single-page scroll content (no nested scroll) */}
      <div className={cls.pageInner}>
        <div className={cls.formCard}>
          <div className={cls.formTop}>
            <div className={cls.formMeta}>
              <div className={cls.formTitle}>{title}</div>
              <div className={cls.formSub}>{subtitle}</div>
            </div>
          </div>

          {/* EXISTING FORM (same structure) */}
          <form className={cls.form} onSubmit={onSubmit} noValidate>
            <fieldset className={cls.options}>
              <legend className="sr-only">{subtitle}</legend>

              {options.map((opt) => {
                const checked = choice === opt.value;

                return (
                  <label
                    key={opt.value}
                    className={`${cls.option} ${
                      checked ? cls.optionActive : ""
                    }`}
                  >
                    <input
                      type="radio"
                      name="gender"
                      value={opt.value}
                      checked={checked}
                      onChange={() => setChoice(opt.value)}
                    />

                    <span className={cls.optionBody}>
                      <span className={cls.optionEmoji} aria-hidden="true">
                        <img
                          className={cls.optionIcon}
                          src={opt.icon}
                          alt=""
                          loading="lazy"
                          draggable="false"
                        />
                      </span>

                      <span className={cls.optionText}>
                        <strong>{opt.title}</strong>
                        <small>{opt.hint}</small>
                      </span>

                      <span className={cls.optionDot} aria-hidden="true" />
                    </span>
                  </label>
                );
              })}
            </fieldset>

            {needsCustom && (
              <div className={cls.sdWrap} role="status" aria-live="polite">
                <label className={cls.sdLabel}>
                  {t("onboarding.gender.selfDescribeLabel", {
                    defaultValue: "Tell us in your words",
                  })}
                  <input
                    type="text"
                    className={cls.sdInput}
                    value={customGender}
                    onChange={(e) => setCustomGender(e.target.value)}
                    placeholder={t("onboarding.gender.selfDescribePh", {
                      defaultValue: "Type here…",
                    })}
                    autoComplete="off"
                  />
                </label>
                <div className={cls.sdHelp}>
                  {t("onboarding.gender.selfDescribeNote", {
                    defaultValue: "This will be shown exactly as you write it.",
                  })}
                </div>
              </div>
            )}

            {/* Keep submit visible-ish, but no nested scroll needed */}
            <div className={cls.actions}>
              <button
                type="submit"
                className={cls.submit}
                disabled={
                  submitting || !choice || (needsCustom && !customGender.trim())
                }
                aria-busy={submitting ? "true" : "false"}
              >
                {submitting
                  ? t("common.saving", { defaultValue: "Saving…" })
                  : cta}
              </button>
            </div>
          </form>

          {/* After-form cards */}
          <section className={cls.cardsSection} aria-label="Gender cards">
            <div className={cls.cardsHeader}>
              <div className={cls.cardsTitle}>
                {t("onboarding.gender.cardsHeader.title", {
                  defaultValue: "A quick vibe check",
                })}
              </div>
              <div className={cls.cardsSub}>
                {t("onboarding.gender.cardsHeader.sub", {
                  defaultValue:
                    "Pick what feels right above. Here’s a little guide, just to make it easier.",
                })}
              </div>
            </div>

            <div className={cls.cardsGrid}>
              {cards.map((c) => (
                <article key={c.key} className={cls.gCard}>
                  <div className={cls.gCardMedia}>
                    <img
                      className={cls.gCardImg}
                      src={c.img}
                      alt=""
                      loading="lazy"
                      draggable="false"
                    />
                    <div className={cls.gCardTint} />
                    <div className={cls.gCardIcon}>
                      <img
                        src={c.IconSrc}
                        alt=""
                        loading="lazy"
                        draggable="false"
                      />
                    </div>
                  </div>

                  <div className={cls.gCardBody}>
                    <div className={cls.gCardTitle}>{c.title}</div>
                    <div className={cls.gCardDesc}>{c.desc}</div>
                  </div>
                </article>
              ))}
            </div>
          </section>
        </div>

        <div className={cls.bottomSpace} />
      </div>
    </main>
  );
}
